import { useId } from "react";
import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import type { CategoryAttributeResponse } from "@/services/api";
import { useCategories } from "../../queries";
import { getCategoryIcon } from "../categories";
import { getCategoryLabel } from "@/features/categories/utils/categoryUtils";

interface CategoriesLinkedModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attribute: CategoryAttributeResponse | null;
}

export function CategoriesLinkedModal({
  open,
  onOpenChange,
  attribute,
}: CategoriesLinkedModalProps) {
  const { data: categories, isLoading } = useCategories();

  const linkedCategories =
    categories?.filter((category) =>
      category.attributes?.some((attr) => attr.attributeId === attribute?.id)
    ) || [];
  const descriptionId = useId();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[600px]"
        aria-describedby={descriptionId}
      >
        <DialogHeader>
          <DialogTitle>Categories Linked to "{attribute?.name}"</DialogTitle>
          <DialogDescription>
            View all categories that use this attribute.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[400px] overflow-y-auto">
          {isLoading ? (
            <div className="py-8 text-center text-sm text-ink-500">
              Loading categories...
            </div>
          ) : linkedCategories.length > 0 ? (
            <div className="space-y-2">
              {linkedCategories.map((category) => {
                const IconComponent = getCategoryIcon(
                  category.icon ?? undefined
                );
                return (
                  <div
                    key={String(category.id)}
                    className="rounded-lg border border-grid-200 bg-white p-4"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-grid-200 bg-white">
                        {IconComponent ? (
                          <IconComponent className="h-6 w-6 text-ink-600" />
                        ) : (
                          <div className="h-4 w-4 rounded-full bg-ink-300" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-ink-900">
                          {getCategoryLabel(category)}
                        </h4>
                        {category.description && (
                          <p className="mt-1 text-sm text-ink-600">
                            {category.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-ink-500">
              No categories linked to this attribute.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
